// src/components/StudentDashboard.jsx
// Student Dashboard - view submissions and upload homework

import React, { useState, useEffect, useRef } from 'react';
import { HelpCircle, Upload, Download, CheckCircle, Clock, AlertCircle, LogOut, Trash, File, Info, AlertTriangle, CloudUpload, Lock, Table2 } from 'lucide-react';
import { createSubmission, getSubmission, getStudentSubmissions, deleteFileFromSubmission, softDeleteSubmission, getAuthenticatedFileUrl, uploadSpreadsheetSource } from '../firebase/submissions';
import { parseUploadedSpreadsheet } from '../utils/parseUploadedSpreadsheet';
import { prepareSpreadsheetUpload } from '../utils/generateSpreadsheetUploadPdf';
import { getHomeworkNames, getMaxMarks, sortHomeworkTypes, hasCustomHomeworkOrder, getExamUnlimitedDurations, getSolutionReleaseSettings, getPdfSpreadsheetAttachmentEnabled } from '../firebase/settings';
import { getEffectiveSubmissionMode, getCohortById, resolveEnabledHomeworkTypes } from '../firebase/cohorts';
import { getAllSolutions } from '../firebase/solutions';
import { getAllQuestionPapers } from '../firebase/questionPapers';
import { logout } from '../firebase/auth';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { DEFAULT_ORG_ID } from '../firebase/orgs';
import { getOrgBranding } from '../firebase/orgBranding';
import { getHeadMarkerName } from '../firebase/headMarkerNotifications';
import { getContrastTextColor } from '../utils/color';
import { openExamPopup } from '../utils/examPopup';
import { getActiveExamSessions } from '../firebase/examSessions';
import { goToCourseHosting, checkCourseHostingAccount } from '../firebase/crossApp';
import HelpPanel from './HelpPanel';
import StudentTutorPanel from './StudentTutorPanel';
import StudentSpreadsheetView from './StudentSpreadsheetView';
import useIsMobile from '../hooks/useIsMobile';

const StudentDashboard = ({ user, onLogout, autoOpenSubmissionId, onAutoOpenSubmissionHandled }) => {
  const isMobile = useIsMobile();
  // The marked submission whose spreadsheet is open (StudentSpreadsheetView)
  const [spreadsheetViewId, setSpreadsheetViewId] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [homeworkNames, setHomeworkNames] = useState({});
  const [cohort, setCohort] = useState(null);
  const [maxMarks, setMaxMarks] = useState({});
  const [solutions, setSolutions] = useState({});
  const [questionPapers, setQuestionPapers] = useState({});
  const [deadlines, setDeadlines] = useState({});
  const [solutionReleaseSettings, setSolutionReleaseSettings] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [addingFileTo, setAddingFileTo] = useState(null); // submissionId currently receiving a new file
  const [addingFileProgress, setAddingFileProgress] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileIndex, setUploadFileIndex] = useState(0);
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);
  const [orgHeaderColor, setOrgHeaderColor] = useState(null);
  const [activeExamSessions, setActiveExamSessions] = useState([]);
  const [headMarkerName, setHeadMarkerName] = useState('your tutor');
  const [showHelp, setShowHelp] = useState(false);
  const [hasCourseHostingAccount, setHasCourseHostingAccount] = useState(false);

  const isAileenOrg = user && (user.orgId || DEFAULT_ORG_ID) === DEFAULT_ORG_ID;

  // Hides the Course Content button for a student with no matching
  // ae-course-hosting account, instead of showing it and having it fail on
  // click - see checkCourseHostingAccount's own comment for why this fails
  // closed (hidden) rather than open.
  useEffect(() => {
    if (!user) return;
    checkCourseHostingAccount().then(setHasCourseHostingAccount);
  }, [user]);

  useEffect(() => {
    if (!user || isAileenOrg) return;
    getOrgBranding(user.orgId || DEFAULT_ORG_ID).then(branding => {
      setOrgLogoUrl(branding?.logos?.student?.url || branding?.logoUrl || null);
      setOrgHeaderColor(branding?.headerColors?.student || null);
    });
  }, [user, isAileenOrg]);

  // Used in error messages below ("contact <name> for assistance") instead
  // of a hardcoded tutor name, since the platform now has more than one org.
  useEffect(() => {
    if (!user) return;
    getHeadMarkerName(user.orgId || DEFAULT_ORG_ID).then(setHeadMarkerName);
  }, [user]);

  // Upload form state
  const [selectedHomework, setSelectedHomework] = useState('TH1');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  // Submission Mode: "Spreadsheet Upload" - a single .xlsx, converted into a
  // PDF (see generateSpreadsheetUploadPdf.js) rather than the multi-file PDF
  // flow above, so kept as its own single-file state.
  const [selectedSpreadsheetFile, setSelectedSpreadsheetFile] = useState(null);
  const spreadsheetInputRef = useRef(null);

  // Exam-mode: which submission mode applies to the currently selected homework
  const [submissionMode, setSubmissionMode] = useState('pdf');
  // Whether the selected homework's exam simulation has no time limit -
  // only non-timed exam-sim homeworks offer the PDF-upload fallback below
  const [isUnlimitedExam, setIsUnlimitedExam] = useState(false);
  // Student has opted into the PDF-upload fallback instead of the exam tool
  // (only offered/relevant when isUnlimitedExam is true)
  const [showPdfFallback, setShowPdfFallback] = useState(false);
  // Whether PDF Upload mode also offers an optional spreadsheet attachment
  // for the currently selected homework type (head marker setting, default off)
  const [pdfSpreadsheetAttachmentAllowed, setPdfSpreadsheetAttachmentAllowed] = useState(false);

  // Whether the next submissions-list update should also advance the
  // selected-homework dropdown - true for a genuine change, set false right
  // before a passive window-focus refresh (see the two effects below)
  const shouldAdvanceSelectionRef = useRef(true);

  // The org's real configured homework types - empty until the head marker
  // adds some via "Add Homework". Never falls back to TH1-RH4: another
  // org's homework types won't look anything like ACCA's. Further narrowed
  // to whichever of those tasks this student's cohort is actually enabled
  // for, or - taking priority when present - this specific student's own
  // enabledHomeworkTypes override (set automatically by ae-course-hosting
  // for a student enrolled in more than one of its courses sharing this
  // cohort; see resolveEnabledHomeworkTypes's own comment). Both default to
  // "everything" when unset, so this is a no-op for every cohort/student
  // predating either feature.
  const homeworkTypes = resolveEnabledHomeworkTypes(cohort, sortHomeworkTypes(Object.keys(homeworkNames)), user);

  // Resolve PDF-vs-exam mode whenever the selected homework type changes
  useEffect(() => {
    let cancelled = false;
    if (!user.cohortId || !selectedHomework) return;
    setShowPdfFallback(false); // don't let the fallback choice leak across homework types
    getEffectiveSubmissionMode(user.cohortId, selectedHomework).then(mode => {
      if (!cancelled) setSubmissionMode(mode);
    });
    getExamUnlimitedDurations().then(unlimitedDurations => {
      if (!cancelled) setIsUnlimitedExam(!!unlimitedDurations[selectedHomework]);
    });
    getPdfSpreadsheetAttachmentEnabled().then(attachmentEnabled => {
      if (!cancelled) setPdfSpreadsheetAttachmentAllowed(!!attachmentEnabled[selectedHomework]);
    });
    return () => { cancelled = true; };
  }, [user.cohortId, selectedHomework]);

  // Cross-app deep link (see App.js/auth.js's consumeInboundSSOToken) — a
  // student redirected here from ae-course-hosting's "view marked work"
  // click carries the exact submission id to open. Navigates straight to
  // the marked PDF via a full-page redirect rather than window.open: this
  // runs inside an async effect well after the original click, so a
  // popup would very likely be blocked by the browser regardless of
  // gesture, whereas a plain navigation isn't. Falls through to the normal
  // dashboard (silently) if the submission isn't actually marked yet, or
  // has no marked file - a stale/premature link shouldn't dead-end.
  useEffect(() => {
    if (!autoOpenSubmissionId) return;
    let cancelled = false;
    (async () => {
      try {
        const submission = await getSubmission(autoOpenSubmissionId);
        const filePath = submission.markedFilePath || submission.markedFileUrl;
        if (submission.status === 'marked' && filePath) {
          const url = await getAuthenticatedFileUrl(filePath);
          if (!cancelled && url) {
            window.location.href = url;
            return;
          }
        }
      } catch (error) {
        console.error('Auto-open marked submission failed:', error);
      }
      if (!cancelled) onAutoOpenSubmissionHandled?.();
    })();
    return () => { cancelled = true; };
  }, [autoOpenSubmissionId]);

  // Load submissions and homework names on mount
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exam mode now runs in a separate window - refresh submissions whenever
  // this tab regains focus, so a just-finished exam submission shows up
  // without the student needing to manually reload. This must NOT advance
  // the selected-homework dropdown below: refocusing the tab also happens
  // for reasons that have nothing to do with a new submission (e.g. the
  // native file picker briefly stealing focus while the student is mid
  // upload), so treating a passive refocus the same as a real submissions
  // change would silently override whatever the student had manually
  // selected.
  useEffect(() => {
    const handleFocus = () => {
      shouldAdvanceSelectionRef.current = false;
      loadSubmissions();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update selected homework to next in sequence when submissions change -
  // but only for a genuine change (initial load, or after an upload/delete/
  // etc.), never for the passive focus-triggered refresh above
  useEffect(() => {
    if (submissions.length >= 0 && user.cohortId) {
      if (shouldAdvanceSelectionRef.current) {
        setSelectedHomework(getNextHomework());
      }
      shouldAdvanceSelectionRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissions, user.cohortId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [submissionsData, names, marks, activeSessions, cohortData, solutionsData, questionPapersData, deadlinesDoc, solutionReleaseData] = await Promise.all([
        getStudentSubmissions(user.uid),
        getHomeworkNames(),
        getMaxMarks(),
        getActiveExamSessions(user.uid).then(sessions => sessions.filter(sess => !sess.orgId || sess.orgId === user.orgId)),
        user.cohortId ? getCohortById(user.cohortId) : Promise.resolve(null),
        getAllSolutions(),
        getAllQuestionPapers(),
        user.cohortId ? getDoc(doc(db, 'cohorts', user.cohortId, 'deadlines', 'current')) : Promise.resolve(null),
        getSolutionReleaseSettings()
      ]);
      setSubmissions(submissionsData);
      setHomeworkNames(names);
      setMaxMarks(marks);
      setActiveExamSessions(activeSessions);
      setCohort(cohortData);
      setSolutions(solutionsData);
      setQuestionPapers(questionPapersData);
      setDeadlines(deadlinesDoc?.exists() ? deadlinesDoc.data() : {});
      setSolutionReleaseSettings(solutionReleaseData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Refresh the "continue task" list when the student comes back to this tab
  // (e.g. after closing the exam popup, or finishing and submitting there) -
  // otherwise a card for an already-submitted session would linger until the
  // next full page load.
  useEffect(() => {
    // Linked tutors: only this tutor's sessions (a session's tutor is fixed when it starts)
    const onFocus = () => { getActiveExamSessions(user.uid).then(sessions => setActiveExamSessions(sessions.filter(sess => !sess.orgId || sess.orgId === user.orgId))); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.uid]);

  const resumeExamSession = (session) => {
    const params = new URLSearchParams({
      homeworkType: session.homeworkType,
      homeworkName: getHomeworkDisplayName(session.homeworkType),
      cohortId: session.cohortId,
      cohortName: session.cohortName || ''
    });
    openExamPopup(`/exam?${params.toString()}`, 'examWindow', { widthPct: 0.95, heightPct: 0.92 });
  };

  const loadSubmissions = async () => {
    try {
      const data = await getStudentSubmissions(user.uid);
      setSubmissions(data);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    }
  };

  const getHomeworkDisplayName = (type) => {
    return homeworkNames[type] || type;
  };

  // Has this submission actually got work in it? A tutor's Reset empties
  // the files but keeps the record, so it mustn't count as handed in - same
  // test as hasSubmittedHomework in functions/index.js. (Before 2026-11-01 a
  // reset homework still showed "Already Submitted" and couldn't be redone.)
  const hasSubmittedWork = (s) =>
    !s.deletedByStudent &&
    ((Array.isArray(s.originalFilePaths) && s.originalFilePaths.length > 0) ||
      (s.fileCount || 0) > 0 || !!s.originalFileUrl);

  // Get submitted homework types for current cohort (excluding soft-deleted and reset)
const getSubmittedHomeworkTypes = () => {
  return submissions
    .filter(s => s.cohortId === user.cohortId && hasSubmittedWork(s))
    .map(s => s.homeworkType);
};

  // A session for a homework already handed in another way (e.g. a PDF
  // upload, or before a cohort move) isn't offered - the server would refuse
  // to resume it anyway (see reconcileExamSession in functions/index.js)
  const continuableExamSessions = activeExamSessions.filter(
    sess => !getSubmittedHomeworkTypes().includes(sess.homeworkType)
  );

  // Get the next homework to submit (smart default)
  const getNextHomework = () => {
    const submitted = getSubmittedHomeworkTypes();

    // Tutor has set their own order: the first not-yet-submitted homework
    // after the furthest one already submitted, in that order
    if (hasCustomHomeworkOrder()) {
      const lastDone = Math.max(-1, ...homeworkTypes.map((t, i) => (submitted.includes(t) ? i : -1)));
      return homeworkTypes.slice(lastDone + 1).find(t => !submitted.includes(t))
        || homeworkTypes.find(t => !submitted.includes(t))
        || homeworkTypes[0] || 'TH1';
    }
    
    // Try to find next in TH sequence first
    const thSubmitted = submitted.filter(h => h.startsWith('TH')).map(h => getHomeworkSequence(h));
    const maxTH = thSubmitted.length > 0 ? Math.max(...thSubmitted) : 0;
    const nextTH = `TH${maxTH + 1}`;
    
    // If next TH exists and is not submitted, use it
    if (homeworkTypes.includes(nextTH) && !submitted.includes(nextTH)) {
      return nextTH;
    }
    
    // Try to find next in RH sequence
    const rhSubmitted = submitted.filter(h => h.startsWith('RH')).map(h => getHomeworkSequence(h));
    const maxRH = rhSubmitted.length > 0 ? Math.max(...rhSubmitted) : 0;
    const nextRH = `RH${maxRH + 1}`;
    
    // If next RH exists and is not submitted, use it
    if (homeworkTypes.includes(nextRH) && !submitted.includes(nextRH)) {
      return nextRH;
    }
    
    // Fall back to first available homework
    const firstAvailable = homeworkTypes.find(type => !submitted.includes(type));
    return firstAvailable || 'TH1';
  };

  // Release date (Deadlines page > "Opens") - until then the homework is
  // shown as locked, with nothing to start, upload or download. The same
  // date is also enforced server-side (createOrResumeExamSession, and the
  // storage rules for its exhibits/requirements/question paper), so this is
  // the friendly version, not the only guard.
  const getReleaseDate = (type) => {
    const availableFrom = deadlines?.[`${type}_availableFrom`];
    const date = availableFrom?.toDate ? availableFrom.toDate() : null;
    return date && date.getTime() > Date.now() ? date : null;
  };
  const formatReleaseDate = (date) => date.toLocaleString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
  });
  const selectedReleaseDate = getReleaseDate(selectedHomework);
  // Unlock by itself if the page is left open past the release time
  const [, setReleaseTick] = useState(0);
  const selectedReleaseMs = selectedReleaseDate ? selectedReleaseDate.getTime() : null;
  useEffect(() => {
    if (!selectedReleaseMs) return;
    const wait = selectedReleaseMs - Date.now() + 1000;
    if (wait > 2147483647) return; // beyond setTimeout's range (~24 days) - a reload will pick it up
    const t = setTimeout(() => setReleaseTick(n => n + 1), wait);
    return () => clearTimeout(t);
  }, [selectedReleaseMs]);

  // Check if a homework can be selected (not already submitted for current cohort)
  const isHomeworkAvailable = (type) => {
    const submitted = getSubmittedHomeworkTypes();
    return !submitted.includes(type);
  };

  // Get the homework sequence number (TH1=1, TH2=2, etc.)
  const getHomeworkSequence = (type) => {
    const match = type.match(/(\d+)$/);
    return match ? parseInt(match[1]) : 0;
  };

  // Check if submitting out of order
  const isOutOfOrder = (type) => {
    const submitted = getSubmittedHomeworkTypes();
    const prefix = type.replace(/\d+$/, ''); // Get TH or RH
    const sequence = getHomeworkSequence(type);
    
    // Check if any previous homework in sequence is missing
    for (let i = 1; i < sequence; i++) {
      const previousType = prefix + i;
      if (!submitted.includes(previousType)) {
        return previousType; // Return the missing homework
      }
    }
    return null;
  };

  // Group submissions by cohort (exclude soft-deleted submissions)
  const groupSubmissionsByCohort = () => {
    const groups = {};
    submissions
      .filter(submission => !submission.deletedByStudent) // Exclude soft-deleted
      .forEach(submission => {
        const cohortKey = submission.cohortId || 'unknown';
        const cohortName = submission.cohortName || 'Unknown Cohort';
        if (!groups[cohortKey]) {
          groups[cohortKey] = {
            cohortId: cohortKey,
            cohortName: cohortName,
            submissions: []
          };
        }
        groups[cohortKey].submissions.push(submission);
      });
    return Object.values(groups);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadError('');
    
    if (files.length === 0) return;
    
    // Validate each file
    const validFiles = [];
    const errors = [];
    
    for (const file of files) {
      // Validate file type
      if (file.type !== 'application/pdf') {
        errors.push(`${file.name}: Must be a PDF file`);
        continue;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name}: File size must be less than 10MB`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (errors.length > 0) {
      setUploadError(errors.join('\n'));
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if leaving the drop zone itself, not child elements
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setUploadError('');

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Validate each file
    const validFiles = [];
    const errors = [];
    
    for (const file of files) {
      // Validate file type
      if (file.type !== 'application/pdf') {
        errors.push(`${file.name}: Must be a PDF file`);
        continue;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name}: File size must be less than 10MB`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (errors.length > 0) {
      setUploadError(errors.join('\n'));
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  // Submission Mode: "Spreadsheet Upload" - single file, light validation
  // here for fast feedback; parseUploadedSpreadsheet re-validates properly
  // (extension, .xlsm rejection, size) as the actual safety net at submit time.
  const handleSpreadsheetFileSelect = (e) => {
    const file = e.target.files[0];
    setUploadError('');
    if (!file) return;

    const nameLower = file.name.toLowerCase();
    if (nameLower.endsWith('.xlsm')) {
      setUploadError('Macro-enabled workbooks (.xlsm) are not supported. Please save as a plain .xlsx file.');
      return;
    }
    if (!nameLower.endsWith('.xlsx')) {
      setUploadError('Please select a .xlsx spreadsheet file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be less than 10MB.');
      return;
    }

    setSelectedSpreadsheetFile(file);
  };

  const handleSpreadsheetDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    handleSpreadsheetFileSelect({ target: { files: [file] } });
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (submissionMode === 'spreadsheet') {
      return handleSpreadsheetUpload();
    }

    // Check if account is suspended
    if (user.suspended) {
      setUploadError('Cannot upload - your account is suspended');
      return;
    }

    if (selectedFiles.length === 0 && !selectedSpreadsheetFile) {
      setUploadError('Please select at least one file');
      return;
    }

    // NEW: Check if homework already submitted for CURRENT cohort
    const submittedTypes = getSubmittedHomeworkTypes();
    if (submittedTypes.includes(selectedHomework)) {
      const displayName = getHomeworkDisplayName(selectedHomework);
      alert(`Homework already submitted!\n\n${displayName} has already been submitted for your current cohort (${user.cohortName || 'your cohort'}).\n\nPlease select the correct homework task to be submitted, or delete this homework from your dashboard and try again.`);
      return;
    }
    
    // NEW: Check if submitting out of order
    const missingHomework = isOutOfOrder(selectedHomework);
    if (missingHomework) {
      const missingName = getHomeworkDisplayName(missingHomework);
      const selectedName = getHomeworkDisplayName(selectedHomework);
      
      if (!window.confirm(`Out of order submission!\n\nYou have not submitted ${missingName} yet.\n\nAre you sure you want to upload ${selectedName}?`)) {
        return;
      }
    }

    // NEW: Check if a deadline has been set for this homework
    try {
      const deadlineDoc = await getDoc(doc(db, 'cohorts', user.cohortId, 'deadlines', 'current'));
      const deadlines = deadlineDoc.exists() ? deadlineDoc.data() : {};
      if (!deadlines[selectedHomework]) {
        const displayName = getHomeworkDisplayName(selectedHomework);
        setUploadError(`Upload not available: No deadline has been set for ${displayName} yet. Please contact ${headMarkerName} for assistance.`);
        return;
      }
      const availableFrom = deadlines[`${selectedHomework}_availableFrom`];
      if (availableFrom?.toMillis && availableFrom.toMillis() > Date.now()) {
        setUploadError(`${getHomeworkDisplayName(selectedHomework)} isn't open yet. It opens on ${formatReleaseDate(availableFrom.toDate())}.`);
        return;
      }
    } catch (err) {
      console.error('Error checking deadline:', err);
      setUploadError(`Unable to verify homework deadline. Please try again or contact ${headMarkerName} for assistance.`);
      return;
    }
    
    // Check if already submitted this homework (pending status) - for adding more files
    const pendingSubmission = submissions.find(s => 
      s.homeworkType === selectedHomework && 
      s.status === 'pending' &&
      s.cohortId === user.cohortId
    );
    
    // A submission that's already been flagged, claimed, is awaiting review,
    // is marked, or was rejected back for revision - a further upload here
    // will be held for the head marker to release, not added immediately
    const inFlightSubmission = submissions.find(s =>
      s.homeworkType === selectedHomework &&
      ['flagged_for_review', 'assigned', 'marking', 'awaiting_review', 'marked', 'rejected'].includes(s.status) &&
      s.cohortId === user.cohortId
    );

    // Total items about to be uploaded in this batch, including the optional
    // spreadsheet attachment (it becomes one more generated-PDF "file")
    const newFileCount = selectedFiles.length + (selectedSpreadsheetFile ? 1 : 0);

    // A reset submission is pending with no files - uploading into it is a
    // fresh start, not "adding more"
    if (pendingSubmission && (pendingSubmission.fileCount || 0) > 0) {
      const displayName = getHomeworkDisplayName(selectedHomework);
      const fileCount = pendingSubmission.fileCount;
      const newTotal = fileCount + newFileCount;

      if (!window.confirm(`You have already uploaded ${fileCount} file${fileCount > 1 ? 's' : ''} for ${displayName}.\n\nThis will add ${newFileCount} more file${newFileCount > 1 ? 's' : ''} (total: ${newTotal}).\n\nContinue?`)) {
        return;
      }
    } else if (inFlightSubmission) {
      const displayName = getHomeworkDisplayName(selectedHomework);

      if (!window.confirm(`${displayName} is already being marked.\n\nYou can still add ${newFileCount === 1 ? 'this page' : 'these pages'}, but it will be held for the head marker to review before it reaches your marker.\n\nContinue?`)) {
        return;
      }
    }
    
    setUploading(true);
    setUploadError('');

    try {
      // A spreadsheet attachment (see "Spreadsheet Attachment" head marker
      // setting) is parsed and converted to a PDF up front, then submitted
      // as one more "file" in the same loop below as a real uploaded PDF -
      // reusing the exact same multi-file merge (FirebaseMarker.jsx) and
      // append logic (submissions.js) a multi-page PDF submission already
      // gets, rather than a separate code path.
      let generatedSpreadsheetFile = null;
      let spreadsheetAnswersForUpload = null;
      let spreadsheetMarkingMode = null;
      if (selectedSpreadsheetFile) {
        ({ file: generatedSpreadsheetFile, spreadsheetAnswers: spreadsheetAnswersForUpload, spreadsheetMarkingMode } = await prepareSpreadsheetUpload({
          studentName: user.name,
          cohortName: user.cohortName || '',
          homeworkType: selectedHomework,
          homeworkName: getHomeworkDisplayName(selectedHomework),
          spreadsheetAnswers: await parseUploadedSpreadsheet(selectedSpreadsheetFile)
        }));
      }

      const filesToUpload = generatedSpreadsheetFile ? [...selectedFiles, generatedSpreadsheetFile] : selectedFiles;

      // Upload each file sequentially, keeping the last submission ID
      let lastSubmissionId;
      let anyQuarantined = false;
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        console.log(`Uploading file ${i + 1}/${filesToUpload.length}: ${file.name}`);
        setUploadFileIndex(i + 1);
        setUploadProgress(0);

        const isSpreadsheetFile = file === generatedSpreadsheetFile;

        const result = await createSubmission({
          studentId: user.uid,
          studentName: user.name,
          studentEmail: user.email,
          studentPhone: user.phone || '',
          cohortId: user.cohortId,
          cohortName: user.cohortName || '',
          homeworkType: selectedHomework,
          file: file,
          onProgress: setUploadProgress,
          submittedVia: (submissionMode === 'exam' && showPdfFallback) ? 'exam_pdf_fallback' : undefined,
          ...(isSpreadsheetFile ? { spreadsheetAnswers: spreadsheetAnswersForUpload, spreadsheetMarkingMode } : {})
        });
        lastSubmissionId = result.id;
        anyQuarantined = anyQuarantined || result.quarantined;
      }

      // Keep the original workbook downloadable too (see FirebaseMarker.jsx's
      // "Download original spreadsheet" link) - non-blocking.
      if (selectedSpreadsheetFile && lastSubmissionId) {
        try {
          await uploadSpreadsheetSource(lastSubmissionId, selectedSpreadsheetFile);
        } catch (e) {
          console.error('Original spreadsheet upload error:', e);
        }
      }

      // Reload submissions
      await loadSubmissions();

      // Notify head marker — use the server timestamp from Firestore so the
      // email always matches the site regardless of the student's device clock
      try {
        const { sendDirectNotification, NOTIFICATION_TYPES } = await import('../firebase/headMarkerNotifications');
        let submittedAt = new Date().toLocaleString('en-GB', { timeZone: 'Europe/London' });
        try {
          const sub = await getSubmission(lastSubmissionId);
          submittedAt = sub.submittedAt.toDate().toLocaleString('en-GB', { timeZone: 'Europe/London' });
        } catch (_) {}
        sendDirectNotification(
          anyQuarantined ? NOTIFICATION_TYPES.ADDITIONAL_PAGES_PENDING : NOTIFICATION_TYPES.NEW_HOMEWORK_SUBMITTED,
          {
            studentName: user.name,
            cohortName: user.cohortName || '',
            homeworkType: homeworkNames[selectedHomework] || selectedHomework,
            submittedAt
          }
        );
      } catch (e) { console.error('Notification error:', e); }

      // Reset form (selectedHomework will be updated by useEffect)
      setShowUpload(false);
      setSelectedFiles([]);
      setSelectedSpreadsheetFile(null);

      const displayName = getHomeworkDisplayName(selectedHomework);
      const fileWord = filesToUpload.length === 1 ? 'file' : 'files';
      if (anyQuarantined) {
        alert(`This submission is already being marked, so your new ${fileWord} for ${displayName} have been sent to the head marker for review before they're added in.`);
      } else {
        alert(`${filesToUpload.length} ${fileWord} added to ${displayName}!\n\nYou can upload more files or wait for marking to begin.`);
      }
      
    } catch (error) {
      console.error('Upload failed:', error);
      if (error.message && error.message.includes('homeworkDeadline')) {
        setUploadError(`Upload failed: No deadline has been configured for this homework. Please contact ${headMarkerName} for assistance.`);
      } else {
        setUploadError(`Upload failed. Please try again or contact ${headMarkerName} if the problem persists.`);
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadFileIndex(0);
    }
  };

  // Submission Mode: "Spreadsheet Upload" - a wholly separate, one-shot flow
  // (mirrors ExamWindowApp.jsx's own handleSubmit): parse the workbook,
  // generate a PDF from it client-side, and submit that PDF through the
  // exact same createSubmission path a manually-uploaded PDF uses. Shares
  // handleUpload's mode-agnostic guard checks (suspended/already-submitted/
  // out-of-order/deadline) by re-running them here rather than threading a
  // third file-type through handleUpload's own append/quarantine logic,
  // which doesn't apply to a mode that always generates a fresh PDF.
  const handleSpreadsheetUpload = async () => {
    if (user.suspended) {
      setUploadError('Cannot upload - your account is suspended');
      return;
    }

    if (!selectedSpreadsheetFile) {
      setUploadError('Please select a spreadsheet file');
      return;
    }

    const submittedTypes = getSubmittedHomeworkTypes();
    if (submittedTypes.includes(selectedHomework)) {
      const displayName = getHomeworkDisplayName(selectedHomework);
      alert(`Homework already submitted!\n\n${displayName} has already been submitted for your current cohort (${user.cohortName || 'your cohort'}).\n\nPlease select the correct homework task to be submitted, or delete this homework from your dashboard and try again.`);
      return;
    }

    const missingHomework = isOutOfOrder(selectedHomework);
    if (missingHomework) {
      const missingName = getHomeworkDisplayName(missingHomework);
      const selectedName = getHomeworkDisplayName(selectedHomework);
      if (!window.confirm(`Out of order submission!\n\nYou have not submitted ${missingName} yet.\n\nAre you sure you want to upload ${selectedName}?`)) {
        return;
      }
    }

    try {
      const deadlineDoc = await getDoc(doc(db, 'cohorts', user.cohortId, 'deadlines', 'current'));
      const deadlines = deadlineDoc.exists() ? deadlineDoc.data() : {};
      if (!deadlines[selectedHomework]) {
        const displayName = getHomeworkDisplayName(selectedHomework);
        setUploadError(`Upload not available: No deadline has been set for ${displayName} yet. Please contact ${headMarkerName} for assistance.`);
        return;
      }
      const availableFrom = deadlines[`${selectedHomework}_availableFrom`];
      if (availableFrom?.toMillis && availableFrom.toMillis() > Date.now()) {
        setUploadError(`${getHomeworkDisplayName(selectedHomework)} isn't open yet. It opens on ${formatReleaseDate(availableFrom.toDate())}.`);
        return;
      }
    } catch (err) {
      console.error('Error checking deadline:', err);
      setUploadError(`Unable to verify homework deadline. Please try again or contact ${headMarkerName} for assistance.`);
      return;
    }

    // A generated-PDF submission has no "add more pages" concept the way a
    // manually-uploaded PDF does - an existing submission for this homework
    // type (of any status) blocks a re-upload rather than appending or
    // quarantining, since there's nothing sensible to append a spreadsheet to.
    const existingSubmission = submissions.find(s =>
      s.homeworkType === selectedHomework &&
      s.cohortId === user.cohortId &&
      hasSubmittedWork(s)
    );
    if (existingSubmission) {
      setUploadError(`You've already submitted this homework. Please contact ${headMarkerName} if you need to resubmit.`);
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const homeworkName = getHomeworkDisplayName(selectedHomework);

      const { file, spreadsheetAnswers, spreadsheetMarkingMode } = await prepareSpreadsheetUpload({
        studentName: user.name,
        cohortName: user.cohortName || '',
        homeworkType: selectedHomework,
        homeworkName,
        spreadsheetAnswers: await parseUploadedSpreadsheet(selectedSpreadsheetFile)
      });

      const result = await createSubmission({
        studentId: user.uid,
        studentName: user.name,
        studentEmail: user.email,
        studentPhone: user.phone || '',
        cohortId: user.cohortId,
        cohortName: user.cohortName || '',
        homeworkType: selectedHomework,
        file,
        onProgress: setUploadProgress,
        spreadsheetAnswers,
        spreadsheetMarkingMode
      });

      // Keep the original workbook downloadable too (see FirebaseMarker.jsx's
      // "Download original spreadsheet" link) - non-blocking, since the
      // submission itself is already complete without it.
      try {
        await uploadSpreadsheetSource(result.id, selectedSpreadsheetFile);
      } catch (e) {
        console.error('Original spreadsheet upload error:', e);
      }

      await loadSubmissions();

      try {
        const { sendDirectNotification, NOTIFICATION_TYPES } = await import('../firebase/headMarkerNotifications');
        sendDirectNotification(NOTIFICATION_TYPES.NEW_HOMEWORK_SUBMITTED, {
          studentName: user.name,
          cohortName: user.cohortName || '',
          homeworkType: homeworkNames[selectedHomework] || selectedHomework,
          submittedAt: new Date().toLocaleString('en-GB', { timeZone: 'Europe/London' })
        });
      } catch (e) { console.error('Notification error:', e); }

      setShowUpload(false);
      setSelectedSpreadsheetFile(null);

      alert(`${homeworkName} submitted!`);
    } catch (error) {
      console.error('Spreadsheet upload failed:', error);
      setUploadError(error.message || `Upload failed. Please try again or contact ${headMarkerName} if the problem persists.`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteFile = async (submissionId, fileIndex, fileUrl) => {
    if (!window.confirm('Delete this file?\n\nThis cannot be undone.')) {
      return;
    }
    
    try {
      const result = await deleteFileFromSubmission(submissionId, fileIndex, fileUrl);
      
      // Reload submissions
      await loadSubmissions();
      
      if (result.deleted) {
        alert('All files deleted - submission removed');
      } else {
        alert('File deleted successfully');
      }
    } catch (error) {
      console.error('Delete file failed:', error);
      if (error.message.includes('Cannot delete files')) {
        alert('Cannot delete files - marking has already started');
      } else {
        alert('Delete failed. Please try again.');
      }
    }
  };

  const handleAddFileToSubmission = async (submission, file) => {
    if (!file) return;
    setAddingFileTo(submission.id);
    setAddingFileProgress(0);
    try {
      const result = await createSubmission({
        studentId: user.uid,
        studentName: user.name,
        studentEmail: user.email,
        studentPhone: user.phone || '',
        cohortId: submission.cohortId,
        cohortName: submission.cohortName,
        homeworkType: submission.homeworkType,
        file,
        onProgress: setAddingFileProgress
      });
      await loadSubmissions();

      if (result.quarantined) {
        try {
          const { sendDirectNotification, NOTIFICATION_TYPES } = await import('../firebase/headMarkerNotifications');
          sendDirectNotification(NOTIFICATION_TYPES.ADDITIONAL_PAGES_PENDING, {
            studentName: user.name,
            cohortName: submission.cohortName || '',
            homeworkType: homeworkNames[submission.homeworkType] || submission.homeworkType,
            submittedAt: new Date().toLocaleString('en-GB', { timeZone: 'Europe/London' })
          });
        } catch (e) { console.error('Notification error:', e); }
        alert("This submission is already being marked, so your new file has been sent to the head marker for review before it's added in.");
      }
    } catch (error) {
      console.error('Add file error:', error);
      alert('Failed to add file. Please try again.');
    } finally {
      setAddingFileTo(null);
      setAddingFileProgress(0);
    }
  };

  const handleDeleteMarkedSubmission = async (submissionId) => {
    if (!window.confirm('Remove this homework from your dashboard?\n\nThis will remove it from your view, but your tutor will still have access to it. You will be able to resubmit this homework if needed.')) {
      return;
    }
    
    try {
      await softDeleteSubmission(submissionId);
      
      // Reload submissions
      await loadSubmissions();
      
      alert('Homework removed from your dashboard');
    } catch (error) {
      console.error('Soft delete submission failed:', error);
      alert('Delete failed. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      onLogout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const [switchingToCourseHosting, setSwitchingToCourseHosting] = useState(false);
  const handleSwitchToCourseHosting = async () => {
    setSwitchingToCourseHosting(true);
    try {
      await goToCourseHosting();
    } catch (error) {
      alert(error.message || 'Could not switch to Course Content.');
      setSwitchingToCourseHosting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'marked':
        return <CheckCircle size={20} color="#10b981" />;
      case 'marking':
      case 'assigned':
        return <Clock size={20} color="#f59e0b" />;
      case 'flagged_for_review':
        return <AlertTriangle size={20} color="#f39c12" />;
      default:
        return <Clock size={20} color="#6b7280" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'marked':
        return 'Marked';
      case 'marking':
        return 'Being Marked';
      case 'assigned':
        return 'Assigned to Marker';
      case 'flagged_for_review':
        return 'More Pages Needed';
      default:
        return 'Submitted';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const headerBg = orgHeaderColor || 'white';

  return (
    <div style={{ minHeight: '100vh', background: '#f7fafc' }}>
      {/* Thin Header Bar */}
      <div style={{
        background: headerBg,
        borderBottom: orgHeaderColor ? 'none' : '1px solid #e2e8f0',
        padding: isMobile ? '10px 12px' : '12px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '10px'
      }}>
        {/* Logo */}
        {isAileenOrg ? (
          <img
            src="/aileen-edgar-logo.png"
            alt="Aileen Edgar"
            style={{ height: isMobile ? '36px' : '50px', width: 'auto', minWidth: 0, maxWidth: isMobile ? '45vw' : 'none', objectFit: 'contain' }}
          />
        ) : orgLogoUrl ? (
          <img
            src={orgLogoUrl}
            alt="Logo"
            style={{ height: isMobile ? '36px' : '50px', width: 'auto', minWidth: 0, maxWidth: isMobile ? '45vw' : 'none', objectFit: 'contain' }}
          />
        ) : (
          <span style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', color: getContrastTextColor(headerBg) }}>
            Homework Solutions
          </span>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '10px', flexShrink: 0 }}>
        {/* Help / FAQs - see HelpPanel.jsx */}
        <button
          type="button"
          onClick={() => setShowHelp(true)}
          title="Help and frequently asked questions"
          style={{ padding: isMobile ? '8px 10px' : '8px 14px', background: '#4a5568', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: isMobile ? '13px' : '14px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <HelpCircle size={16} />
          {!isMobile && 'Help'}
        </button>
        {/* Course Content Button - only for a student with a matching
            ae-course-hosting account, see checkCourseHostingAccount above */}
        {hasCourseHostingAccount && (
        <button
          onClick={handleSwitchToCourseHosting}
          disabled={switchingToCourseHosting}
          style={{
            padding: isMobile ? '8px 10px' : '8px 16px',
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: switchingToCourseHosting ? 'not-allowed' : 'pointer',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600'
          }}
        >
          {switchingToCourseHosting ? 'Switching...' : (isMobile ? 'Course' : 'Course Content')}
        </button>
        )}

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          style={{
            padding: isMobile ? '8px 10px' : '8px 16px',
            background: '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '6px' : '8px',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600'
          }}
        >
          <LogOut size={16} />
          Logout
        </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: isMobile ? '12px' : '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Welcome Section */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: isMobile ? '16px' : '24px',
          marginBottom: isMobile ? '12px' : '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '20px' : '24px', fontWeight: '700', color: '#2d3748' }}>
            Welcome, {user.name}
          </h1>
          <p style={{ margin: 0, color: '#718096' }}>
            Upload your homework and track your submissions below
          </p>
          <StudentTutorPanel user={user} />
        </div>

        {/* Continue an in-progress timed/typed task - the session (and its timer,
            if any) keeps running server-side even after this window is closed
            or the student logs out, so this is just a way back into it. */}
        {continuableExamSessions.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: isMobile ? '16px' : '24px',
            marginBottom: isMobile ? '12px' : '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '2px solid #c33329'
          }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Continue Your Task
            </h2>
            <div style={{ display: 'grid', gap: '12px' }}>
              {continuableExamSessions.map(session => (
                <div
                  key={session.id}
                  style={{
                    padding: '16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr auto',
                    gap: isMobile ? '12px' : '16px',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '18px', color: '#2d3748' }}>
                      {getHomeworkDisplayName(session.homeworkType)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '13px', color: '#718096' }}>
                      <Clock size={14} />
                      {session.status === 'not_started'
                        ? 'Not started yet'
                        : session.unlimitedTime ? 'In progress - no time limit' : 'In progress - timer is still running'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => resumeExamSession(session)}
                    style={{
                      padding: '10px 20px',
                      background: '#c33329',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Continue
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Section */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: isMobile ? '16px' : '24px',
          marginBottom: isMobile ? '12px' : '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          {/* Suspended Account Warning */}
          {user.suspended && (
            <div style={{
              background: '#fef3c7',
              border: '2px solid #f59e0b',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={24} color="#f59e0b" />
              <div>
                <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>
                  Account Suspended
                </div>
                <div style={{ fontSize: '14px', color: '#92400e' }}>
                  Your account has been suspended. You can view your previous submissions but cannot upload new homework.
                  {user.suspensionReason && ` Reason: ${user.suspensionReason}`}
                </div>
              </div>
            </div>
          )}
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: showUpload ? '20px' : 0
          }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
              Start or Upload Homework
            </h2>
            {!user.suspended && (
              <button
                onClick={() => setShowUpload(!showUpload)}
                style={{
                  padding: '10px 20px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                <Upload size={16} />
                {showUpload ? 'Hide' : 'Start / Upload Homework'}
              </button>
            )}
          </div>

          {showUpload && !user.suspended && (
            <form onSubmit={handleUpload} style={{ marginTop: '20px' }}>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{
                  padding: '10px 14px',
                  background: '#f4f6f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#4a5568',
                  lineHeight: 1.6
                }}>
                  <strong>1.</strong> Choose the homework or mock you want to start from the dropdown below.<br />
                  <strong>2.</strong> Click the button that appears to launch it (or upload a completed PDF, if that's an option for this task).
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    fontSize: '14px',
                    color: '#4a5568'
                  }}>
                    Select Homework Type
                  </label>
                  <select
                    value={selectedHomework}
                    onChange={(e) => setSelectedHomework(e.target.value)}
                    disabled={uploading}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'white',
                      cursor: uploading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {homeworkTypes.map(type => {
                      const available = isHomeworkAvailable(type);
                      const isNext = type === getNextHomework();
                      return (
                        <option 
                          key={type} 
                          value={type}
                          disabled={!available}
                          style={{
                            color: available ? 'inherit' : '#a0aec0'
                          }}
                        >
                          {getHomeworkDisplayName(type)}{isNext && available ? '  > Next' : ''}{!available ? ' (Already Submitted)' : ''}{available && getReleaseDate(type) ? ` (opens ${getReleaseDate(type).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {selectedReleaseDate ? (
                  <div style={{
                    padding: '24px',
                    background: '#f7fafc',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <Lock size={28} color="#718096" style={{ marginBottom: '10px' }} />
                    <p style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '700', color: '#2c3e50' }}>
                      {getHomeworkDisplayName(selectedHomework)} isn't open yet
                    </p>
                    <p style={{ margin: 0, fontSize: '14px', color: '#4a5568' }}>
                      It opens on {formatReleaseDate(selectedReleaseDate)}.
                    </p>
                  </div>
                ) : submissionMode === 'exam' && !showPdfFallback ? (
                  <div style={{
                    padding: '24px',
                    background: '#eef2ff',
                    border: '2px solid #c7d2fe',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <p style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#2c3e50' }}>
                      This assignment should be completed in the homework app.
                    </p>
                    <p style={{ margin: '0 0 16px 0', fontSize: '17px', fontWeight: '800', color: '#c33329' }}>
                      {isUnlimitedExam
                        ? <>This is <u>not</u> a timed submission</>
                        : <>This <u>IS</u> a timed submission</>}
                    </p>
                    {isMobile && (
                      <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#4a5568' }}>
                        This works best on a computer - the answer screen is hard to use on a phone.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        const params = new URLSearchParams({
                          homeworkType: selectedHomework,
                          homeworkName: getHomeworkDisplayName(selectedHomework),
                          cohortId: user.cohortId,
                          cohortName: user.cohortName || ''
                        });
                        openExamPopup(`/exam?${params.toString()}`, 'examWindow', { widthPct: 0.95, heightPct: 0.92 });
                      }}
                      style={{
                        padding: '12px 28px',
                        background: '#c33329',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '700',
                        fontSize: '15px'
                      }}
                    >
                      Click here to start
                    </button>
                    {isUnlimitedExam && (
                      <div style={{ marginTop: '14px' }}>
                        <button
                          type="button"
                          onClick={() => setShowPdfFallback(true)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#4a5568',
                            fontSize: '13px',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          Prefer to upload a completed PDF instead?
                        </button>
                      </div>
                    )}
                  </div>
                ) : submissionMode === 'spreadsheet' ? (
                  <>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#4a5568'
                      }}>
                        Select Spreadsheet File
                      </label>

                      <div
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleSpreadsheetDrop}
                        onClick={() => !uploading && spreadsheetInputRef.current?.click()}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: isMobile ? '20px' : '32px',
                          border: isDragging ? '3px dashed #667eea' : '2px dashed #cbd5e0',
                          borderRadius: '12px',
                          background: isDragging ? '#f0f4ff' : '#f7fafc',
                          transition: 'all 0.2s ease',
                          cursor: uploading ? 'not-allowed' : 'pointer',
                          textAlign: 'center',
                          boxSizing: 'border-box'
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                          <CloudUpload
                            size={48}
                            color={isDragging ? '#667eea' : '#a0aec0'}
                            style={{ transition: 'color 0.2s ease' }}
                          />
                          <div>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: isDragging ? '#667eea' : '#4a5568', marginBottom: '4px' }}>
                              {isDragging ? 'Drop your spreadsheet here' : isMobile ? 'Tap to choose your spreadsheet' : 'Drag and drop your spreadsheet here'}
                            </div>
                            <div style={{ fontSize: '14px', color: '#718096', marginBottom: '12px' }}>
                              {isMobile ? '.xlsx file' : 'or click to browse'}
                            </div>
                          </div>

                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <span style={{
                              padding: '8px 20px',
                              background: uploading ? '#cbd5e0' : '#667eea',
                              color: 'white',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '600',
                              display: 'inline-block',
                              cursor: uploading ? 'not-allowed' : 'pointer'
                            }}>
                              Browse Files
                            </span>
                            <input
                              ref={spreadsheetInputRef}
                              type="file"
                              accept=".xlsx"
                              onClick={(e) => e.stopPropagation()}
                              onChange={handleSpreadsheetFileSelect}
                              disabled={uploading}
                              style={{
                                position: 'absolute',
                                inset: 0,
                                opacity: 0,
                                cursor: uploading ? 'not-allowed' : 'pointer',
                                width: '100%',
                                height: '100%'
                              }}
                            />
                          </div>

                          <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                            .xlsx only, max 10MB
                          </div>
                        </div>
                      </div>

                      {selectedSpreadsheetFile && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          background: '#f0fdf4',
                          border: '1px solid #bbf7d0',
                          borderRadius: '8px'
                        }}>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#166534',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px'
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <File size={14} />
                              {selectedSpreadsheetFile.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => setSelectedSpreadsheetFile(null)}
                              disabled={uploading}
                              style={{
                                background: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                cursor: uploading ? 'not-allowed' : 'pointer',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <Trash size={12} />
                              Remove
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
                      <button
                        type="submit"
                        disabled={!selectedSpreadsheetFile || uploading}
                        style={{
                          padding: '10px 24px',
                          background: (!selectedSpreadsheetFile || uploading) ? '#cbd5e0' : '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: (!selectedSpreadsheetFile || uploading) ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <Upload size={16} />
                        {uploading ? `Uploading ${uploadProgress}%` : 'Submit Spreadsheet'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowUpload(false);
                          setSelectedSpreadsheetFile(null);
                          setUploadError('');
                        }}
                        disabled={uploading}
                        style={{
                          padding: '10px 24px',
                          background: '#e2e8f0',
                          color: '#4a5568',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: uploading ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        Cancel
                      </button>
                    </div>

                    {uploading && (
                      <div style={{ marginTop: '4px', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#667eea', transition: 'width 0.2s ease' }} />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                {submissionMode === 'exam' && showPdfFallback && (
                  <div style={{ marginBottom: '-4px' }}>
                    <button
                      type="button"
                      onClick={() => setShowPdfFallback(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#4a5568',
                        fontSize: '13px',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      &larr; Back to exam simulation
                    </button>
                  </div>
                )}
                {questionPapers[selectedHomework]?.filePath && (
                  <div style={{ marginBottom: '-4px' }}>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const url = await getAuthenticatedFileUrl(questionPapers[selectedHomework].filePath);
                          if (url) {
                            window.open(url, '_blank');
                          } else {
                            alert('Unable to access the question paper. Please contact support.');
                          }
                        } catch (error) {
                          console.error('Error opening question paper:', error);
                          alert('Error accessing the question paper. Please try again.');
                        }
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        background: '#eef2ff',
                        color: '#4338ca',
                        border: '2px solid #c7d2fe',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      <Download size={16} />
                      Download Questions
                    </button>
                  </div>
                )}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    fontSize: '14px',
                    color: '#4a5568'
                  }}>
                    Select PDF File
                  </label>

                  {/* Drag and Drop Zone */}
                  <div
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: isMobile ? '20px' : '32px',
                      border: isDragging ? '3px dashed #667eea' : '2px dashed #cbd5e0',
                      borderRadius: '12px',
                      background: isDragging ? '#f0f4ff' : '#f7fafc',
                      transition: 'all 0.2s ease',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      textAlign: 'center',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <CloudUpload
                        size={48}
                        color={isDragging ? '#667eea' : '#a0aec0'}
                        style={{ transition: 'color 0.2s ease' }}
                      />
                      <div>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: isDragging ? '#667eea' : '#4a5568',
                          marginBottom: '4px'
                        }}>
                          {isDragging ? 'Drop your PDFs here' : isMobile ? 'Tap to choose your PDFs' : 'Drag and drop your PDFs here'}
                        </div>
                        <div style={{
                          fontSize: '14px',
                          color: '#718096',
                          marginBottom: '12px'
                        }}>
                          {isMobile ? 'You can pick more than one' : 'or click to browse (multiple files supported)'}
                        </div>
                      </div>

                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <span
                          style={{
                            padding: '8px 20px',
                            background: uploading ? '#cbd5e0' : '#667eea',
                            color: 'white',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'inline-block',
                            cursor: uploading ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Browse Files
                        </span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf"
                          multiple
                          onClick={(e) => e.stopPropagation()}
                          onChange={handleFileSelect}
                          disabled={uploading}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0,
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            width: '100%',
                            height: '100%'
                          }}
                        />
                      </div>

                      <div style={{
                        fontSize: '12px',
                        color: '#a0aec0'
                      }}>
                        PDF only, max 10MB
                      </div>
                    </div>
                  </div>
                  
                  {selectedFiles.length > 0 && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: '8px'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#166534',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <CheckCircle size={16} />
                        Selected {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}:
                      </div>
                      <div style={{ display: 'grid', gap: '6px' }}>
                        {selectedFiles.map((file, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 10px',
                              background: 'white',
                              borderRadius: '6px',
                              fontSize: '13px',
                              border: '1px solid #bbf7d0'
                            }}
                          >
                            <span style={{
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: '#166534',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <File size={14} />
                              {file.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeSelectedFile(index)}
                              disabled={uploading}
                              style={{
                                background: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                cursor: uploading ? 'not-allowed' : 'pointer',
                                fontWeight: '600',
                                marginLeft: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <Trash size={12} />
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {pdfSpreadsheetAttachmentAllowed && (
                  <div>
                    <label style={{
                      display: 'block',
                      marginTop: '16px',
                      marginBottom: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#4a5568'
                    }}>
                      Attach a Spreadsheet (optional)
                    </label>

                    <div
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDrop={handleSpreadsheetDrop}
                      onClick={() => !uploading && spreadsheetInputRef.current?.click()}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '20px',
                        border: isDragging ? '3px dashed #667eea' : '2px dashed #cbd5e0',
                        borderRadius: '12px',
                        background: isDragging ? '#f0f4ff' : '#f7fafc',
                        transition: 'all 0.2s ease',
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        textAlign: 'center',
                        boxSizing: 'border-box'
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        <CloudUpload size={32} color={isDragging ? '#667eea' : '#a0aec0'} style={{ transition: 'color 0.2s ease' }} />
                        <div style={{ fontSize: '14px', color: '#718096' }}>
                          Drag and drop, or click to browse
                        </div>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <span style={{
                            padding: '6px 16px',
                            background: uploading ? '#cbd5e0' : '#667eea',
                            color: 'white',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            display: 'inline-block',
                            cursor: uploading ? 'not-allowed' : 'pointer'
                          }}>
                            Browse Files
                          </span>
                          <input
                            ref={spreadsheetInputRef}
                            type="file"
                            accept=".xlsx"
                            onClick={(e) => e.stopPropagation()}
                            onChange={handleSpreadsheetFileSelect}
                            disabled={uploading}
                            style={{
                              position: 'absolute',
                              inset: 0,
                              opacity: 0,
                              cursor: uploading ? 'not-allowed' : 'pointer',
                              width: '100%',
                              height: '100%'
                            }}
                          />
                        </div>
                        <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                          .xlsx only, max 10MB
                        </div>
                      </div>
                    </div>

                    {selectedSpreadsheetFile && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '13px', color: '#166534', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <File size={14} />
                          {selectedSpreadsheetFile.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedSpreadsheetFile(null)}
                          disabled={uploading}
                          style={{
                            background: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '11px',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Trash size={12} />
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
                  <button
                    type="submit"
                    disabled={(selectedFiles.length === 0 && !selectedSpreadsheetFile) || uploading}
                    style={{
                      padding: '10px 24px',
                      background: ((selectedFiles.length === 0 && !selectedSpreadsheetFile) || uploading) ? '#cbd5e0' : '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: ((selectedFiles.length === 0 && !selectedSpreadsheetFile) || uploading) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Upload size={16} />
                    {uploading
                      ? `Uploading ${uploadProgress}%${selectedFiles.length > 1 ? ` (file ${uploadFileIndex}/${selectedFiles.length})` : ''}`
                      : selectedFiles.length > 0
                        ? `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}${selectedSpreadsheetFile ? ' + Spreadsheet' : ''}`
                        : selectedSpreadsheetFile
                          ? 'Upload Spreadsheet'
                          : 'Upload Files'
                    }
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowUpload(false);
                      setSelectedFiles([]);
                      setSelectedSpreadsheetFile(null);
                      setUploadError('');
                    }}
                    disabled={uploading}
                    style={{
                      padding: '10px 24px',
                      background: '#e2e8f0',
                      color: '#4a5568',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Cancel
                  </button>
                </div>

                {uploading && (
                  <div style={{
                    marginTop: '4px',
                    height: '8px',
                    background: '#e2e8f0',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${uploadProgress}%`,
                      height: '100%',
                      background: '#667eea',
                      transition: 'width 0.2s ease'
                    }} />
                  </div>
                )}
                  </>
                )}
              </div>

              {uploadError && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#991b1b',
                  fontSize: '14px',
                  whiteSpace: 'pre-line'
                }}>
                  {uploadError}
                </div>
              )}

              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                color: '#166534',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <AlertTriangle size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <strong>Important:</strong> {submissionMode === 'spreadsheet' ? '.xlsx spreadsheet files only, max 10MB.' : 'PDF files only, max 10MB.'} You'll receive feedback within 48 hours.
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Submissions List */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: isMobile ? '16px' : '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
            My Submissions
            {user.cohortName && (
              <span style={{ 
                marginLeft: '12px', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#667eea',
                background: '#eef2ff',
                padding: '4px 12px',
                display: 'inline-block',
                borderRadius: '6px'
              }}>
                {user.cohortName}
              </span>
            )}
          </h2>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
              Loading your submissions...
            </div>
          ) : submissions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
              <AlertCircle size={48} style={{ margin: '0 auto 16px' }} />
              <p style={{ margin: 0, fontSize: '16px' }}>No submissions yet</p>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>Upload your first homework to get started!</p>
            </div>
          ) : (
            // Group submissions by cohort
            groupSubmissionsByCohort().map((cohortGroup, groupIndex) => (
              <div key={cohortGroup.cohortId} style={{ marginBottom: groupIndex < groupSubmissionsByCohort().length - 1 ? '32px' : '0' }}>
                {/* Cohort Header - Only show if multiple cohorts */}
                {groupSubmissionsByCohort().length > 1 && (
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: cohortGroup.cohortId === user.cohortId ? '#667eea' : '#718096',
                    marginBottom: '12px',
                    padding: '8px 12px',
                    background: cohortGroup.cohortId === user.cohortId ? '#eef2ff' : '#f7fafc',
                    borderRadius: '6px',
                    border: cohortGroup.cohortId === user.cohortId ? '2px solid #667eea' : '1px solid #e2e8f0'
                  }}>
                    {cohortGroup.cohortName}
                    {cohortGroup.cohortId === user.cohortId && (
                      <span style={{ 
                        marginLeft: '8px',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#667eea'
                      }}>
                        (Current Cohort)
                      </span>
                    )}
                  </div>
                )}
                
                {/* Submissions for this cohort */}
                <div style={{ display: 'grid', gap: '12px' }}>
                  {cohortGroup.submissions.map((submission) => (
                <div
                  key={submission.id}
                  style={{
                    padding: '16px',
                    border: cohortGroup.cohortId === user.cohortId ? '2px solid #e2e8f0' : '2px solid #f7fafc',
                    borderRadius: '8px',
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '200px 1fr auto',
                    gap: isMobile ? '12px' : '16px',
                    alignItems: 'center',
                    opacity: cohortGroup.cohortId === user.cohortId ? 1 : 0.75,
                    background: cohortGroup.cohortId === user.cohortId ? 'white' : '#fafafa'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '18px', color: '#2d3748' }}>
                      {getHomeworkDisplayName(submission.homeworkType)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#a0aec0', marginTop: '4px' }}>
                      {submission.homeworkType}
                    </div>
                  </div>

<div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      {getStatusIcon(submission.status)}
                      <span style={{ fontWeight: '600', fontSize: '14px' }}>
                        {getStatusText(submission.status)}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#718096' }}>
                      Submitted: {formatDate(submission.submittedAt)}
                    </div>
                    {submission.status === 'marked' && submission.markedAt && (
                      <div style={{ fontSize: '13px', color: '#718096' }}>
                        Marked: {formatDate(submission.markedAt)}
                      </div>
                    )}
                    
                    {submission.isLate && (
                      <div style={{ 
                        fontSize: '13px', 
                        color: '#e74c3c',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginTop: '4px'
                      }}>
                        <AlertTriangle size={14} />
                        Late Submission ({Math.floor(submission.minutesLate / 60)}h {submission.minutesLate % 60}m late)
                      </div>
                    )}
                    
                    {/* Files List */}
                    {submission.fileCount > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#4a5568', marginBottom: '6px' }}>
                          Files ({submission.fileCount}):
                        </div>
                        <div style={{ display: 'grid', gap: '4px' }}>
                          {(() => {
                            // Sort the base files by name for consistent display (matches
                            // marker view); any released additions (see releasePendingAdditions)
                            // are appended after, unsorted, matching the marking screen's order
                            const baseFileNames = submission.fileNames || [];
                            const baseIndices = baseFileNames.map((_, i) => i);
                            baseIndices.sort((a, b) => {
                              const nameA = baseFileNames[a] || '';
                              const nameB = baseFileNames[b] || '';
                              return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
                            });
                            const fileNames = [...baseIndices.map(i => baseFileNames[i]), ...(submission.releasedAdditionFileNames || [])];
                            const fileIndices = fileNames.map((_, i) => i);

                            return fileIndices.map((originalIndex) => {
                              const fileName = fileNames[originalIndex];
                              return (
                                <div
                                  key={originalIndex}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '6px 8px',
                                    background: '#f7fafc',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    border: '1px solid #e2e8f0'
                                  }}
                                >
                                  <span style={{ 
                                    flex: 1, 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap',
                                    color: '#2d3748',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}>
                                    <File size={14} />
                                    {fileName}
                                  </span>
                                  {submission.status === 'pending' && (
                                    <button
                                      onClick={() => {
                                        const filePaths = submission.originalFilePaths || submission.originalFileUrls || [];
                                        handleDeleteFile(submission.id, originalIndex, filePaths[originalIndex]);
                                      }}
                                      style={{
                                        background: '#e74c3c',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        fontSize: '11px',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        marginLeft: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                      title="Delete this file"
                                    >
                                      <Trash size={12} />
                                      Delete
                                    </button>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                        {/* Always available - late uploads after marking has started are
                            quarantined by createSubmission and held for head marker release */}
                        <div style={{ marginTop: '8px' }}>
                            <label style={{ cursor: addingFileTo === submission.id ? 'not-allowed' : 'pointer' }}>
                              <input
                                type="file"
                                accept=".pdf"
                                style={{ display: 'none' }}
                                disabled={addingFileTo === submission.id}
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleAddFileToSubmission(submission, e.target.files[0]);
                                    e.target.value = '';
                                  }
                                }}
                              />
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                background: addingFileTo === submission.id ? '#a0aec0' : '#667eea',
                                color: 'white',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                pointerEvents: addingFileTo === submission.id ? 'none' : 'auto'
                              }}>
                                <Upload size={12} />
                                {addingFileTo === submission.id ? `Uploading ${addingFileProgress}%` : 'Add File'}
                              </span>
                            </label>
                          </div>
                      </div>
                    )}
                  </div>

                  <div style={isMobile
                    ? { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }
                    : { display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                    {submission.status === 'marked' && submission.score && (
                      <div style={{
                        background: '#10b981',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontWeight: '700',
                        fontSize: '18px'
                      }}>
                        {submission.score.total}/{maxMarks[submission.homeworkType] || 20}
                      </div>
                    )}
                    {submission.status === 'marked' && (submission.markedFilePath || submission.markedFileUrl) && (
                      <>
                        <button
                          onClick={async () => {
                            try {
                              // Use new path format if available, fall back to old URL format
                              const filePath = submission.markedFilePath || submission.markedFileUrl;
                              const url = await getAuthenticatedFileUrl(filePath);
                              if (url) {
                                window.open(url, '_blank');
                              } else {
                                alert('Unable to access marked homework. Please contact support.');
                              }
                            } catch (error) {
                              console.error('Error opening marked homework:', error);
                              alert('Error accessing marked homework. Please try again.');
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            background: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          <Download size={16} />
                          View Feedback
                        </button>
                        {submission.markedSpreadsheet && submission.spreadsheetAnswers && (
                          <button
                            onClick={() => setSpreadsheetViewId(submission.id)}
                            style={{
                              padding: '8px 16px',
                              background: '#1b998b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '14px',
                              fontWeight: '600'
                            }}
                          >
                            <Table2 size={16} />
                            Marked Spreadsheet
                          </button>
                        )}
                        {spreadsheetViewId === submission.id && (
                          <StudentSpreadsheetView
                            submission={submission}
                            homeworkName={getHomeworkDisplayName(submission.homeworkType)}
                            onClose={() => setSpreadsheetViewId(null)}
                          />
                        )}
                        {solutions[submission.homeworkType]?.filePath && (() => {
                          // Tutor-controlled per homework type: wait for the cohort
                          // deadline too (default/unset, the original behavior) or
                          // release as soon as this submission is marked (false).
                          const waitsForDeadline = solutionReleaseSettings[submission.homeworkType] !== false;
                          const deadlineTs = deadlines[submission.homeworkType];
                          const deadlineDate = deadlineTs?.toDate ? deadlineTs.toDate() : (deadlineTs ? new Date(deadlineTs) : null);
                          const deadlinePassed = !waitsForDeadline || !deadlineDate || deadlineDate <= new Date();
                          if (!deadlinePassed) return null;
                          return (
                            <button
                              onClick={async () => {
                                try {
                                  const url = await getAuthenticatedFileUrl(solutions[submission.homeworkType].filePath);
                                  if (url) {
                                    window.open(url, '_blank');
                                  } else {
                                    alert('Unable to access the model solution. Please contact support.');
                                  }
                                } catch (error) {
                                  console.error('Error opening model solution:', error);
                                  alert('Error accessing the model solution. Please try again.');
                                }
                              }}
                              style={{
                                padding: '8px 16px',
                                background: '#1b998b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '14px',
                                fontWeight: '600'
                              }}
                            >
                              <File size={16} />
                              View Model Solution
                            </button>
                          );
                        })()}
                        <button
                          onClick={() => handleDeleteMarkedSubmission(submission.id)}
                          style={{
                            padding: '8px 16px',
                            background: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                          title="Delete this marked homework"
                        >
                          <Trash size={16} />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
        </div>
      </div>
      {showHelp && <HelpPanel role="student" tutorName={headMarkerName} onClose={() => setShowHelp(false)} />}
    </div>
  );
};

export default StudentDashboard;